import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import BrandBar from '../BrandBar';
import { api, ApiError } from '../lib/api';
import { clearKioskDeviceToken, getKioskDeviceToken } from '../lib/kioskToken';
import MemberPicker from './MemberPicker';
import BeverageGrid from './BeverageGrid';
import QuantityStepper from './QuantityStepper';
import { kistenText } from './kisten';
import CorrectionToggle from './CorrectionToggle';
import UndoList from './UndoList';
import SammelgastSettleDialog from './SammelgastSettleDialog';
import PersonKartenDialog from './PersonKartenDialog';
import type {
  KioskBeverage,
  KioskMember,
  KioskSessionResponse,
  RecordedConsumption,
  ZuruecknehmbareBuchung,
} from './types';
import { formatEuroCents } from '../lib/format';
import { CardTerminalIcon, UsersIcon } from '../admin/NavIcons';

const UNDO_WINDOW_SECONDS = 120; // mirrors backend UNDO_WINDOW_MS (ADR-0004); UI-only countdown

// Mirrors backend/src/domain/types.ts's SAMMELGAST_MEMBER_ID (no shared
// types package between frontend/backend, project convention) -- keep in
// sync. Duplicated from MemberPicker.tsx rather than shared, same reason.
const SAMMELGAST_MEMBER_ID = 'sammelgast';

// After a real event (2026-07-25) the operator reported the next person in the
// queue booking onto the previous member's tab: whoever booked simply walked
// away and left their name selected. The screen therefore returns to the member
// list by itself once nothing has been touched for this long. Purely a UI
// convenience -- nothing is written or cancelled by it.
const IDLE_RETURN_SECONDS = 20;
// Only surface the countdown near the end; a permanently ticking number would
// rush people who are still deciding what to drink.
const IDLE_HINT_FROM_SECONDS = 10;

/**
 * 2026-09-25 (Owner): Der Kiosk bringt seine obere Leiste jetzt selbst mit, statt sie von der
 * gemeinsamen Hülle zu bekommen. Grund: Eventname und Modus sollen DORT stehen -- das ist die
 * einzige Stelle im Bild, an der sie nichts an Höhe kosten, und auf dem Tablet zählt jede Zeile.
 *
 * Der schwarze Kopfblock darunter ist im Sammelgast-Modus dadurch ganz entfallen.
 */
function Shell({ info, children }: { info?: ReactNode; children: ReactNode }) {
  return (
    <div className="app-shell">
      <BrandBar title="BCT" subtitle="Speisen- und Getränkeerfassung" to="/kiosk">
        {info}
      </BrandBar>
      {children}
    </div>
  );
}

/**
 * Was der Bestätigungsbalken über die eben erfasste Buchung sagt.
 *
 * 2026-09-26 (Owner): „Jetzt gibt es immer noch den Hinweis auf »20 x Bier Marie erfasst…« im
 * Rückgängig-Balken."
 *
 * An einer Entnahme standen dort zwei Zahlen, die nicht hingehören:
 *
 * 1. **Der Preis je Stück** ist der VERKAUFSpreis. Die Abteilung zahlt ihn nicht — sie wird einen
 *    Monat später über das Monatsblatt zu EINKAUFSpreisen belastet. Dieselbe sinnlose Zahl wie
 *    der offene Saldo, nur an einer anderen Stelle.
 * 2. **„20x"** ist nicht, was jemand getan hat. Getippt wurde „1 Kiste". Eine Bestätigung, die
 *    etwas anderes sagt als die Handlung, taugt nicht zum Gegenlesen — und Gegenlesen ist ihr
 *    ganzer Zweck, solange der Rückgängig-Knopf danebensteht.
 *
 * Die Stückzahl bleibt trotzdem stehen, in Klammern: Das Monatsblatt zählt Stück, und wer die
 * Entnahme am Regal nachzählt, zählt Flaschen. Weggelassen wäre sie nicht einfacher, sondern
 * ungeprüft.
 */
function bestaetigungsText(
  buchung: RecordedConsumption & { crates?: number },
  entnahme: boolean,
): string {
  const { quantity, beverageName, priceCents, crates } = buchung;
  if (!entnahme) {
    return `${quantity}x ${beverageName} (${formatEuroCents(priceCents)} pro Stück) erfasst.`;
  }
  // Ohne Kistenwahl wurde auch an einer Entnahme in Stück gebucht -- dann bleibt nur der Preis weg.
  if (crates === undefined) return `${quantity}x ${beverageName} erfasst.`;
  // Das Vorzeichen gehört an BEIDE Zahlen. „1 Kiste (−20 Stück)" wäre ein Widerspruch im selben
  // Satz, und eine Korrekturbuchung ist genau der Fall, in dem man den Balken zweimal liest.
  const vz = quantity < 0 ? '−' : '';
  return `${vz}${kistenText(crates)} ${beverageName} (${vz}${Math.abs(quantity)} Stück) erfasst.`;
}

/**
 * Sagt der Server einen GRUND, oder ist das Gerät abgemeldet?
 *
 * 2026-10-02, beim Bauen der Monatsevent-Absage gefunden und NICHT gemeldet worden: Der Kiosk hat
 * jede 403 als „Gerät abgemeldet" behandelt und den Gerätetoken weggeworfen. Die Routen antworten
 * aber mit 403 auch dann, wenn fachlich etwas nicht geht -- Artikel nicht im Abteilungs-Sortiment,
 * Mitglied am Kiosk ausgeblendet, und seit dem 01.10. die beiden Entnahme-Absagen, die
 * ausdrücklich den Grund nennen. **Diese Sätze hat nie jemand gesehen:** Das Tablet meldete sich
 * stattdessen ab und verlangte eine Neueinrichtung, die ein Abteilungsleiter machen muss.
 *
 * Unterschieden wird am KÖRPER der Antwort: Unsere Routen schicken `{ error: "<deutscher Satz>" }`,
 * die Ablehnung des Autorisierers am API Gateway dagegen `{ message: "Forbidden" }`. Der Satz ist
 * also genau dann da, wenn es etwas zu sagen gibt.
 */
function istFachlicheAbsage(e: unknown): e is ApiError {
  return (
    e instanceof ApiError &&
    e.status === 403 &&
    typeof e.body === 'object' &&
    e.body !== null &&
    'error' in e.body
  );
}

export default function KioskScreen() {
  const deviceToken = getKioskDeviceToken();
  const [session, setSession] = useState<KioskSessionResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [eventClosed, setEventClosed] = useState(false);
  const [deviceInvalid, setDeviceInvalid] = useState(false);
  const [selectedMember, setSelectedMember] = useState<KioskMember>();
  const [balanceCents, setBalanceCents] = useState<number>();
  /**
   * 2026-10-02: Was für die gewählte Person zurückgenommen werden kann -- am Monatsevent der Ersatz
   * für den Korrektur-Schalter. Kommt vom SERVER, nicht aus einer Liste, die dieser Bildschirm
   * selbst mitschreibt: Eine mitgeschriebene Liste kennt die Buchungen vom geteilten Tablet nebenan
   * nicht, und genau die sind der Fall, um den es geht.
   */
  const [zuruecknehmbar, setZuruecknehmbar] = useState<ZuruecknehmbareBuchung[]>([]);
  const [quantity, setQuantity] = useState(1);
  // v0.12 FR-203/FR-205: whether the next booking is a correction (negative
  // quantity). Kept separate from `quantity` itself -- the stepper always
  // shows/edits a plain 1..20 magnitude; this decides the sign applied to it.
  const [correctionMode, setCorrectionMode] = useState(false);
  // 2026-09-26: Die gewählte Kistenzahl an einem Entnahme-Event. Sie steht neben der Stückzahl,
  // weil erst die Kachel den Artikel -- und damit das Gebinde -- kennt.
  const [crates, setCrates] = useState<number | undefined>(undefined);
  /**
   * Die Buchung, über die der Bestätigungsbalken spricht — samt der KISTENZAHL, mit der sie
   * getippt wurde. Der Server kennt nur Stück; wie viele Kisten das waren, weiß allein dieser
   * Bildschirm, und die Auswahl selbst ist nach der Buchung schon wieder aufgehoben
   * (`setCrates(undefined)`). Deshalb reist sie mit der Buchung mit, statt in einem zweiten
   * Zustand daneben zu liegen, der auseinanderlaufen könnte.
   */
  const [lastConsumption, setLastConsumption] = useState<
    RecordedConsumption & { crates?: number }
  >();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [recording, setRecording] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  /**
   * 2026-10-15 (v0.17 Stufe 3): „Mit Karte bezahlen" für eine Person. Festgehalten wird, WER bezahlt
   * -- nicht die aktuelle Auswahl: Das Pop-up zeigt bis zum Ende die Person, für die es geöffnet
   * wurde, auch wenn sich die Auswahl darunter ändern sollte.
   */
  const [kartePerson, setKartePerson] = useState<{
    memberId: string;
    name: string;
    balanceCents: number;
  }>();
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(IDLE_RETURN_SECONDS);
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const idleTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const idleDeadlineRef = useRef(0);

  const loadSession = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    setEventClosed(false);
    try {
      const data = await api.get<KioskSessionResponse>('/kiosk/session', 'kiosk');
      setSession(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setEventClosed(true);
      } else if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        // The device token is dead/expired/revoked (ADR-0004) -- this is not
        // a transient connection error, so don't offer "retry" for it. Clear
        // the stale token and send the tablet back to kiosk setup.
        clearKioskDeviceToken();
        setDeviceInvalid(true);
      } else {
        setError('Verbindung zum Server fehlgeschlagen. Bitte erneut versuchen.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (deviceToken) void loadSession();
  }, [deviceToken, loadSession]);

  const loadBalance = useCallback(async (memberId: string) => {
    try {
      const { totalCents, zuruecknehmbar } = await api.get<{
        totalCents: number;
        zuruecknehmbar?: ZuruecknehmbareBuchung[];
      }>(`/kiosk/members/${memberId}/balance`, 'kiosk');
      setBalanceCents(totalCents);
      setZuruecknehmbar(zuruecknehmbar ?? []);
    } catch {
      setBalanceCents(undefined);
      setZuruecknehmbar([]);
    }
  }, []);

  // v0.11 FR-197: in Sammelgast-Modus there is no selected person -- the
  // server ignores/overrides memberId unconditionally for a guest-mode
  // event (never trusts the client for identity here, same posture as
  // price), so the frontend simply omits it. Computed here (not after the
  // early-return guards below) so the balance-load effect right below can
  // depend on it -- hooks must run unconditionally on every render.
  const guestModeEnabled = session?.event.guestModeEnabled ?? false;
  // 2026-09-26: Eine Entnahme bucht wie der Sammelgast ohne Personenauswahl -- aber auf die
  // ABTEILUNG, und es wird niemand kassiert: kein Saldo, kein „Abrechnen".
  const entnahme = session?.event.kind === 'ENTNAHME';
  const ohnePersonenauswahl = guestModeEnabled || entnahme;
  const showCratePresets = session?.event.showCratePresets ?? false;
  /**
   * 2026-10-02 (Owner-Fund): Am Monatsevent gibt es keinen Korrektur-Schalter mehr.
   *
   * **Ausgeblendet, nicht ausgegraut** -- dieselbe Entscheidung wie beim Gast-Häkchen am 22.09.:
   * Ein grauer Schalter ohne Erklärung liest sich wie ein Defekt, und erklärt wird er hier durch
   * das, was an seine Stelle tritt (die Rücknahmeliste unter dem Raster).
   *
   * Die Vorgabe bei fehlendem Feld ist `true`: Ein Tablet mit neuem Bundle gegen einen älteren
   * Server soll den Schalter behalten, statt aus einer fehlenden Angabe ein Verbot zu lesen. Die
   * Regel selbst steht im Schreibweg, nicht hier.
   */
  const korrekturSichtbar = session?.event.showCorrection ?? true;
  // Welche Artikel keine Kiste kennen -- Maß Goiß kommt vom Fass, Kaffee aus der Maschine.
  const ohneGebinde = (session?.beverages ?? []).filter((b) => !b.unitsPerCrate).map((b) => b.name);
  // Solange noch gar kein Gebinde gepflegt ist, wären das ALLE Artikel -- eine Meldung mit
  // zwanzig Namen liest niemand. Drei nennen, den Rest zählen.
  const ohneGebindeText =
    ohneGebinde.length <= 3
      ? ohneGebinde.join(', ')
      : `${ohneGebinde.slice(0, 3).join(', ')} und ${ohneGebinde.length - 3} weitere`;

  // v0.13 FR-212: Sammelgast-Modus has no `selectMember` call to trigger the
  // first balance load (there is no picker) -- load it once the session
  // confirms guest-mode is on.
  useEffect(() => {
    if (guestModeEnabled) void loadBalance(SAMMELGAST_MEMBER_ID);
  }, [guestModeEnabled, loadBalance]);

  // Returning to the member list -- the single path used by both switch
  // buttons and by the idle timer, so all three behave identically.
  const clearMember = useCallback(() => {
    setSelectedMember(undefined);
    setLastConsumption(undefined);
    setBalanceCents(undefined);
    // Die Liste gehört der Person, nicht dem Bildschirm -- stehen gelassen wäre sie beim nächsten
    // Mitglied die Buchungsliste des vorigen, und ein Tipp darauf nähme dessen Buchung zurück.
    // Dieselbe Klasse wie das Abteilungs-Sortiment am 28.08. und das Korrekturpanel am 27.08.
    setZuruecknehmbar([]);
    setQuantity(1);
    setCrates(undefined);
    setCorrectionMode(false); // v0.12: a correction is never left armed across a person switch
  }, []);

  // Idle countdown: runs only while a member is selected, and any touch or key
  // press anywhere on the screen restarts it. Deliberately still runs while the
  // undo banner is up -- that is exactly the moment someone walks off and
  // leaves their name on screen for the next person to book against.
  //
  // The deadline lives in a ref and the expiry fires from inside the interval.
  // An earlier version derived expiry from the countdown state in a second
  // effect, which meant that after one auto-return the state sat at 0 and the
  // NEXT member selection was cleared instantly -- the kiosk became unusable
  // after the first timeout. Unit tests missed it because each test mounted a
  // fresh component; only the deployed app showed it.
  //
  // 2026-10-15 (Stufe 3): RUHT, solange „Mit Karte bezahlen" offen ist. Wer die Karte ans Terminal
  // hält, berührt das Tablet nicht -- nach 20 s wäre die Person sonst mitten in der Zahlung
  // abgewählt. Schließt das Pop-up, beginnt die Frist von vorn.
  useEffect(() => {
    if (!selectedMember || kartePerson) return;
    const armDeadline = () => {
      idleDeadlineRef.current = Date.now() + IDLE_RETURN_SECONDS * 1000;
    };
    armDeadline();
    setIdleSecondsLeft(IDLE_RETURN_SECONDS);

    idleTimerRef.current = setInterval(() => {
      const left = Math.max(0, Math.ceil((idleDeadlineRef.current - Date.now()) / 1000));
      setIdleSecondsLeft(left);
      if (left === 0) {
        clearInterval(idleTimerRef.current);
        clearMember();
      }
    }, 1000);

    document.addEventListener('pointerdown', armDeadline);
    document.addEventListener('keydown', armDeadline);
    return () => {
      clearInterval(idleTimerRef.current);
      document.removeEventListener('pointerdown', armDeadline);
      document.removeEventListener('keydown', armDeadline);
    };
  }, [selectedMember, clearMember, kartePerson]);

  useEffect(() => {
    if (!lastConsumption) return;
    setSecondsLeft(UNDO_WINDOW_SECONDS);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [lastConsumption]);

  // Checked first: clearing the token on an invalid/expired/revoked session
  // re-renders with deviceToken now falsy, and this dedicated message must
  // win over the generic "never set up" one below so the operator
  // understands *why* the device needs re-setup.
  if (deviceInvalid) {
    return (
      <Shell>
        <div className="content">
          <h1>Gerät abgemeldet</h1>
          <p>
            Dieses Gerät wurde vom Server abgemeldet (z.B. Bindung aufgehoben oder Sitzung
            abgelaufen). Bitte richten Sie den Kiosk neu ein.
          </p>
          <Link to="/admin/kiosk-setup" className="primary-button">
            Gerät neu einrichten
          </Link>
        </div>
      </Shell>
    );
  }

  if (!deviceToken) {
    return (
      <Shell>
        <div className="content">
          <h1>Kiosk nicht eingerichtet</h1>
          <p>
            Dieses Gerät ist noch keinem Event zugeordnet. Bitte im Adminbereich anmelden und ein
            Event für diesen Kiosk auswählen.
          </p>
          <Link to="/admin/kiosk-setup" className="primary-button">
            Zur Kiosk-Einrichtung
          </Link>
        </div>
      </Shell>
    );
  }

  if (loading)
    return (
      <Shell>
        <p className="content">Lädt…</p>
      </Shell>
    );

  if (eventClosed) {
    return (
      <Shell>
        <div className="content">
          <h1>Event beendet</h1>
          <p>Das an diesem Gerät hinterlegte Event wurde geschlossen. Bitte ein neues auswählen.</p>
          <Link to="/admin/kiosk-setup" className="primary-button">
            Neues Event auswählen
          </Link>
        </div>
      </Shell>
    );
  }

  if (error || !session) {
    return (
      <Shell>
        <div className="content">
          <div className="error-banner">{error ?? 'Unbekannter Fehler'}</div>
          <button className="primary-button" onClick={() => void loadSession()}>
            Erneut versuchen
          </button>
        </div>
      </Shell>
    );
  }

  const selectMember = (member: KioskMember) => {
    setSelectedMember(member);
    setQuantity(1);
    setCorrectionMode(false);
    setBalanceCents(undefined);
    setZuruecknehmbar([]);
    void loadBalance(member.memberId);
  };

  const recordConsumption = async (beverage: KioskBeverage) => {
    if (!ohnePersonenauswahl && !selectedMember) return;
    // 2026-09-26: Ist eine Kistenzahl gewählt, ergibt sich die Stückzahl erst hier -- aus dem
    // Gebinde DIESES Artikels. Ohne Gebinde gibt es keine Kiste, und dann wird nicht geraten.
    //
    // Diese Prüfung steht VOR `setRecording(true)`, und das ist nicht Kosmetik: In der ersten
    // Fassung stand sie danach und sprang mit `return` heraus -- am `finally` vorbei, das
    // `recording` zurücksetzt. Danach war jede Kachel dauerhaft gesperrt, und der Kiosk sah aus,
    // als hinge er (Owner-Meldung, 2026-09-26). Ein früher Ausstieg darf nie hinter dem Setzen
    // eines Zustands liegen, den nur ein `finally` wieder aufräumt.
    let magnitude = quantity;
    if (crates !== undefined) {
      if (!beverage.unitsPerCrate) {
        // Letzte Verteidigungslinie, über die Oberfläche nicht erreichbar: Das Raster sperrt
        // Artikel ohne Gebinde, sobald eine Kistenzahl gewählt ist, und der Hinweis darüber sagt
        // es vorher. Hier steht sie trotzdem, damit aus einem künftigen Umbau am Raster keine
        // falsche Buchung wird -- NIE `setError`: das ersetzt den ganzen Kiosk durch eine
        // Fehlerseite (`if (error || !session)`).
        return;
      }
      magnitude = Math.round(crates * beverage.unitsPerCrate);
    }
    setRecording(true);
    setError(undefined);
    // v0.12 FR-203: correctionMode flips the sign of the stepper's plain
    // 1..20 magnitude -- the server independently re-validates the range
    // (ADR-0004: the client's control is convenience only).
    const signedQuantity = correctionMode ? -magnitude : magnitude;
    try {
      const result = await api.post<RecordedConsumption>(
        '/kiosk/consumptions',
        {
          ...(selectedMember ? { memberId: selectedMember.memberId } : {}),
          beverageId: beverage.beverageId,
          quantity: signedQuantity,
        },
        'kiosk',
      );
      setLastConsumption({ ...result, crates });
      setQuantity(1); // FR-64: the stepper resets so the next tap defaults back to 1
      /**
       * 2026-09-27 (Owner): „Wenn ich eine Korrektur durchführe, z. B. Minus 1 Kiste Bier Marie,
       * steht unten im Balken »−1 Bier Marie erfasst«."
       *
       * Der Balken sagte die Wahrheit — gebucht war wirklich EINE FLASCHE. Die Kistenwahl wurde
       * hier nach jeder Buchung gelöscht, also fiel der nächste Tipp stumm auf Stück zurück: aus
       * −20 Flaschen wurden −1, und im Monatsblatt fehlten der Abteilung 19, ohne dass irgendwo
       * etwas auffällig aussah. Genau die Fehlerklasse, die dieses Projekt schon mehrfach
       * erwischt hat (`normalizeQuantity` v0.12, der Namens-Schnappschuss 28.08.).
       *
       * **Die Kistenwahl bleibt jetzt stehen.** An einer Entnahme ist die Kiste die REGEL, nicht
       * die Ausnahme — wer fünf Kisten holt, tippt einmal „1 Kiste" und dann fünf Kacheln. Sie
       * nach jeder Buchung zu löschen war nicht nur lästig, es machte den nächsten Tipp
       * gefährlich.
       *
       * **Der Korrektur-Schalter wird weiterhin entschärft** (Zeile unten), und das ist kein
       * Widerspruch: Er kehrt ein VORZEICHEN um, das man einer Kachel nicht ansieht. Die
       * Kistenwahl dagegen ist sichtbar — der Knopf steht gedrückt, die Menge zeigt einen Strich
       * statt einer Zahl, Artikel ohne Gebinde sind grau, und der Balken darunter nennt sie.
       * Ein versehentlich scharfer Korrektur-Schalter ist unsichtbar, eine versehentlich scharfe
       * Kiste nicht.
       */
      // v0.12: never leave a correction armed for the next tap -- a booking
      // that just happened, positive or negative, always starts the next one
      // fresh (R-1's mitigation: this is the riskier mode, so it must not
      // silently persist).
      setCorrectionMode(false);
      // The balance came back WITH the booking -- no second request, and no
      // second pair of Lambda executions per tap. `loadBalance` stays for the
      // initial load and for switching person.
      setBalanceCents(result.balanceCents);
      // 2026-10-02: Die Rücknahmeliste ebenso -- sonst wäre die eben erfasste Buchung erst nach
      // einem Personenwechsel darin zu finden.
      setZuruecknehmbar(result.zuruecknehmbar ?? []);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setEventClosed(true);
      } else if (istFachlicheAbsage(e)) {
        // Den Satz des Servers zeigen, statt das Gerät abzumelden. Trifft seit dem 02.10. auch das
        // Tablet, das noch ein altes Bundle hält und deshalb weiter einen Korrektur-Schalter
        // anbietet, den es am Monatsevent nicht mehr gibt -- sieben Geräte hängen dort.
        setError(e.message);
      } else if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        clearKioskDeviceToken();
        setDeviceInvalid(true);
      } else {
        setError('Erfassung fehlgeschlagen -- keine Verbindung. Bitte erneut versuchen.');
      }
    } finally {
      setRecording(false);
    }
  };

  // v0.11: in Sammelgast-Modus there is no `selectedMember` at all (FR-197),
  // so undo must not require one -- it used to, which silently no-opped
  // every Sammelgast-mode undo tap (the countdown kept running with nothing
  // the operator could do about it).
  /**
   * Eine Buchung zurücknehmen -- der eine Weg für beide Griffe.
   *
   * `sk` kommt entweder vom Bestätigungsbalken (die eben erfasste Buchung) oder aus der
   * Rücknahmeliste am Monatsevent. Zwei Funktionen dafür wären zwei Stellen, an denen der Balken
   * stehen bleibt, nachdem die Zeile weg ist.
   */
  const zuruecknehmen = async (sk: string) => {
    try {
      const { balanceCents, zuruecknehmbar } = await api.del<{
        balanceCents: number;
        zuruecknehmbar?: ZuruecknehmbareBuchung[];
      }>('/kiosk/consumptions/last', 'kiosk', { sk });
      // Der Balken spricht über genau diese Buchung -- bleibt er mit seinem „Rückgängig" stehen,
      // führt der nächste Druck ins Leere (404). Betrifft nur den Fall, dass aus der LISTE
      // zurückgenommen wurde, was der Balken gerade meldet.
      setLastConsumption((b) => (b?.sk === sk ? undefined : b));
      // Same as the booking path: the corrected balance travels with the undo.
      setBalanceCents(balanceCents);
      setZuruecknehmbar(zuruecknehmbar ?? []);
    } catch {
      setError('Storno fehlgeschlagen. Zeitfenster evtl. abgelaufen.');
    }
  };

  const undo = async () => {
    if (!lastConsumption) return;
    await zuruecknehmen(lastConsumption.sk);
  };

  // 2026-09-17: „Bezahlt – neuer Gast" in the Abrechnen dialog. The guest who just
  // paid has left: their last booking can no longer be taken back (the server
  // refuses it too), so the undo banner goes with them.
  const onSettled = (newBalanceCents: number) => {
    setSettleOpen(false);
    setLastConsumption(undefined);
    setBalanceCents(newBalanceCents);
  };

  // v0.11 FR-197: Sammelgast-Modus skips person-selection entirely -- straight
  // to quantity + the item grid, at the guest price, with no picker and no
  // "switch person" affordance (there is no person to switch from).
  const showGuestPrice = guestModeEnabled || (selectedMember?.isGuest ?? false);

  // Der Eventname und der Modus stehen in der oberen Leiste (siehe `Shell`) -- im Bild ohnehin
  // vorhanden, dort kosten sie keine Zeile.
  //
  // 2026-09-26 (Owner): Auch der Mitgliedsname steht jetzt hier oben statt in einem eigenen
  // schwarzen Block. Er bekommt dafür eine eigene, GRÖSSERE Auszeichnung als die Modus-Marke:
  // Er beantwortet „auf wessen Deckel läuft das?", und das Missverständnis darüber hat am
  // 25.07.2026 zu Buchungen auf dem Deckel der vorigen Person geführt. Klein genug, um
  // übersehen zu werden, darf er nie sein -- die Zeile spart er trotzdem.
  const kopfInfo = (
    <span className="kiosk-topbar-info">
      {guestModeEnabled && <span className="kiosk-topbar-mode">Sammelgast</span>}
      {entnahme && <span className="kiosk-topbar-mode">Entnahme</span>}
      {selectedMember && (
        <span className="kiosk-topbar-member">
          {selectedMember.firstName} {selectedMember.lastName}
          {selectedMember.isGuest && ' (Gast)'}
        </span>
      )}
      <span className="kiosk-topbar-event">{session.event.name}</span>
    </span>
  );

  /**
   * 2026-09-27 (Owner, nach dem Wunsch der Bediener nach Gruppen): „Rückgängig" sitzt jetzt IN
   * der Bedienzeile statt als fixierter Balken am unteren Rand.
   *
   * **Der Grund ist Platz, und zwar gemessen.** Mit dem echten Sortiment des Heimspiel-Events
   * (13 alkoholisch / 11 alkoholfrei / 6 Speisen, 1280 px breit, 9 Spalten) kostet die
   * Dreiteilung des Rasters eine ganze Kachelreihe: 670 px Seitenhöhe werden zu 782. Der Balken
   * allein hielt davon 96 px frei -- er liegt fixiert unten, und die Seite muss Platz lassen,
   * damit er die unterste Kachelreihe nicht verdeckt. In der Bedienzeile kostet er NICHTS: Sie
   * ist ohnehin da, und Saldo, „Abrechnen", Buchungstext und „Rückgängig" passen zusammen in eine
   * Zeile (gemessen: 56 px, kein Umbruch). Damit kostet die Gruppierung netto 16 px statt 112.
   *
   * Zweiter Gewinn, den niemand bestellt hat: Der Balken verdeckte zwei Minuten lang den unteren
   * Bildschirmrand. Der Knopf steht jetzt immer an derselben Stelle.
   *
   * 2026-10-18 (Owner): „Storno" statt „Rückgängig", und zwar überall, wo eine Buchung
   * zurückgenommen wird (Kiosk, Rücknahmeliste am Monatsevent, Handy). Anlass war Platz: Mit
   * „Mit Karte" (seit 15.10.) passte die Zeile nach einer Buchung bei 1280 px nicht mehr --
   * „Storno (118s)" ist 42 px schmaler als „Rückgängig (118s)" (gemessen).
   */
  const rueckgaengig =
    lastConsumption && secondsLeft > 0 ? (
      <>
        <span className="kiosk-undo-text">{bestaetigungsText(lastConsumption, entnahme)}</span>
        <button className="kiosk-undo-button" onClick={() => void undo()}>
          Storno ({secondsLeft}s)
        </button>
      </>
    ) : null;

  return (
    <Shell info={kopfInfo}>
      <div className="content kiosk-screen">
        {error && <div className="error-banner">{error}</div>}
        {/* 2026-09-26: Am Render abgeleitet, nicht gespeichert -- wer die Kistenwahl aufhebt,
            sieht den Hinweis von selbst verschwinden (gleiche Bauart wie der Filter-Hinweis vom
            29.08.). Er steht VOR dem Griff: Eine graue Kachel ohne Erklärung liest sich wie ein
            Defekt. */}
        {crates !== undefined && ohneGebinde.length > 0 && (
          <div className="bt-notice-banner">
            Für {ohneGebindeText} gibt es keine Kiste — diese Artikel sind gerade nicht antippbar.
            Heben Sie die Kistenwahl auf, um sie in Stück zu buchen.
          </div>
        )}
        {kartePerson && (
          <PersonKartenDialog
            memberId={kartePerson.memberId}
            name={kartePerson.name}
            balanceCents={kartePerson.balanceCents}
            terminalName={session.event.kartenterminal}
            onClose={() => setKartePerson(undefined)}
            onPaid={(neu) => {
              setKartePerson(undefined);
              setBalanceCents(neu);
            }}
            onEventClosed={() => {
              setKartePerson(undefined);
              setEventClosed(true);
            }}
            onDeviceInvalid={() => {
              setKartePerson(undefined);
              clearKioskDeviceToken();
              setDeviceInvalid(true);
            }}
          />
        )}
        {ohnePersonenauswahl ? (
          <div>
            {/* 2026-09-25 (Owner): Im Sammelgast-Modus gibt es KEINEN schwarzen Kopfblock mehr.
              Was darin stand, war entweder unveränderlich („Buchung für", „Sammelgast") oder
              gehört nach oben in die Leiste (der Eventname). Übrig bleiben die zwei Dinge, die
              wirklich gebraucht werden -- der offene Betrag und der Weg zum Kassieren --, und
              die passen in die Bedienzeile. Das spart gegenüber gestern noch einmal rund
              110 px, also mehr als eine ganze Kachelreihe. */}
            {settleOpen && (
              <SammelgastSettleDialog
                kartenzahlung={session?.event.kartenzahlung === true}
                terminalName={session?.event.kartenterminal}
                onClose={() => setSettleOpen(false)}
                onSettled={onSettled}
                onEventClosed={() => {
                  setSettleOpen(false);
                  setEventClosed(true);
                }}
                onDeviceInvalid={() => {
                  setSettleOpen(false);
                  clearKioskDeviceToken();
                  setDeviceInvalid(true);
                }}
              />
            )}
            {/* Menge, Korrektur, Saldo und Abrechnen in EINER Zeile. Menge zuerst -- sie wird oft
              angefasst, die Korrektur selten; der Betrag steht direkt neben dem Knopf, mit dem
              er kassiert wird. */}
            <div className="kiosk-controls">
              <QuantityStepper
                quantity={quantity}
                onChange={setQuantity}
                crates={crates}
                onCratesChange={setCrates}
                showCratePresets={showCratePresets}
              />
              {korrekturSichtbar && (
                <CorrectionToggle
                  active={correctionMode}
                  quantity={quantity}
                  crates={crates}
                  onToggle={() => setCorrectionMode((v) => !v)}
                />
              )}
              {rueckgaengig}
              {/* 2026-09-26 (Owner): Bei einer Entnahme wird niemand kassiert -- kein Saldo,
                kein „Abrechnen". Die Abteilung holt Kisten ab und wird dafür über das Monatsblatt
                zu Einkaufspreisen belastet; ein „Offener Saldo" aus Verkaufspreisen wäre eine
                Zahl, die nach einer Schuld aussieht und keine ist. Der Server liefert sie hier
                gar nicht erst (siehe `openBalanceCents`). */}
              {!entnahme && (
                <>
                  <div className="kiosk-controls-gap" />
                  {/* 2026-09-17: der offene Betrag gehört dem Gast an DIESEM Tablet seit seiner
                    letzten Abrechnung, nicht dem Event -- „(dieses Event)" wäre bei zwei Tablets
                    falsch. */}
                  <span className="kiosk-balance">
                    Offener Saldo (aktueller Gast):{' '}
                    <strong>
                      {balanceCents === undefined ? '…' : formatEuroCents(balanceCents)}
                    </strong>
                  </span>
                  {/* 2026-09-17: Kassiert wird über einen Dialog, der die Einzelposten zeigt. Bei
                    0,00 € gesperrt -- es gibt niemanden abzurechnen. */}
                  <button
                    className="kiosk-settle-button"
                    disabled={!balanceCents}
                    onClick={() => setSettleOpen(true)}
                  >
                    Abrechnen
                  </button>
                </>
              )}
            </div>
            <BeverageGrid
              beverages={session.beverages}
              disabled={recording}
              onSelect={(b) => void recordConsumption(b)}
              showGuestPrice={showGuestPrice}
              requireCrateSize={crates !== undefined}
            />
          </div>
        ) : !selectedMember ? (
          <MemberPicker
            eventDivisionId={session.event.divisionId}
            members={session.members}
            onSelect={selectMember}
          />
        ) : (
          <div>
            {/* 2026-09-26 (Owner): auch hier kein schwarzer Block mehr. Der Name steht oben in
              der Leiste (siehe `kopfInfo`), Saldo und der Weg zurück in der Bedienzeile --
              dieselbe Form wie im Sammelgast-Modus. */}
            <div className="kiosk-controls">
              <QuantityStepper
                quantity={quantity}
                onChange={setQuantity}
                crates={crates}
                onCratesChange={setCrates}
                showCratePresets={showCratePresets}
              />
              {korrekturSichtbar && (
                <CorrectionToggle
                  active={correctionMode}
                  quantity={quantity}
                  crates={crates}
                  onToggle={() => setCorrectionMode((v) => !v)}
                />
              )}
              {rueckgaengig}
              <div className="kiosk-controls-gap" />
              {idleSecondsLeft <= IDLE_HINT_FROM_SECONDS && (
                <span className="kiosk-idle-hint" role="status">
                  Zurück in {idleSecondsLeft} s
                </span>
              )}
              {/* FR-63/AC-45: this event's running total only, shown plainly (O-2).
                  2026-10-18 (Owner): „Offen:" statt „Offener Saldo (dieses Event):" -- 166 px
                  schmaler, der größte Hebel für die volle Zeile. Dass es um DIESES Event geht,
                  sagt der Eventname in der oberen Leiste. */}
              <span className="kiosk-balance">
                Offen:{' '}
                <strong>{balanceCents === undefined ? '…' : formatEuroCents(balanceCents)}</strong>
              </span>
              {/* 2026-10-15 (Stufe 3, FR-262): nur mit Terminal am Tablet und offenem Saldo. */}
              {session.event.kartenzahlung === true && (balanceCents ?? 0) > 0 && (
                <button
                  className="kiosk-settle-button bt-icon-text"
                  aria-label="Mit Karte bezahlen"
                  onClick={() =>
                    setKartePerson({
                      memberId: selectedMember.memberId,
                      name: `${selectedMember.firstName} ${selectedMember.lastName}`.trim(),
                      balanceCents: balanceCents ?? 0,
                    })
                  }
                >
                  {/* 2026-10-18: Symbol VOR dem Wort, nie statt (Konvention §2, Kiosk). */}
                  <CardTerminalIcon />
                  Mit Karte
                </button>
              )}
              {/* 2026-10-08 (Owner, Vorschlag 10): Personen statt „↩" -- siehe CorrectionToggle. */}
              <button className="kiosk-settle-button bt-icon-text" onClick={clearMember}>
                <UsersIcon />
                Anderes Mitglied
              </button>
            </div>
            <BeverageGrid
              beverages={session.beverages}
              disabled={recording}
              onSelect={(b) => void recordConsumption(b)}
              showGuestPrice={showGuestPrice}
              requireCrateSize={crates !== undefined}
            />
            {/**
             * 2026-10-02: Was an die Stelle des Korrektur-Schalters tritt -- die Buchungen DIESER
             * Person aus den letzten 15 Minuten, jede mit ihrem eigenen „Storno" (bis 18.10. „Zurücknehmen").
             *
             * **Sie steht UNTER dem Raster, und das ist gemessen entschieden.** Über dem Raster
             * kostete sie bei jeder Buchung Höhe, also auf dem Weg, der ≤3 Tipps dauern soll
             * (NFR-1) -- dieselbe Rechnung wie bei der Gruppierung am 27.09., wo eine zusätzliche
             * Zeile 112 px kostete. Wer etwas berichtigen will, schaut ohnehin genauer hin; wer
             * nur bucht, soll sie nicht sehen müssen.
             *
             * Sie erscheint nur, wenn es etwas zurückzunehmen gibt. Ein leerer Kasten mit der
             * Überschrift „Zurücknehmen" wäre an 364 von 365 Tagen das Erste unter dem Raster und
             * würde die Liste zu einem Möbelstück machen, das niemand mehr liest.
             */}
            <UndoList
              name={`${selectedMember.firstName} ${selectedMember.lastName}`}
              buchungen={zuruecknehmbar}
              disabled={recording}
              onZuruecknehmen={(sk) => void zuruecknehmen(sk)}
            />
            {/**
             * 2026-10-02 (Owner): Der breite Balken „↩ Anderes Mitglied auswählen" unter dem Raster
             * ist WEG. Oben rechts in der Bedienzeile steht derselbe Griff.
             *
             * **Seine Begründung war richtig und hat sich selbst aufgehoben.** Er stand dort, weil
             * das Auge nach dem Antippen beim Raster ist und die obere Zeile beim Scrollen aus dem
             * Bild läuft — nur war ER der Grund, dass überhaupt so weit gescrollt werden musste:
             * 68 px Knopf plus Abstände und Trennlinie, zusammen 110 px.
             *
             * Gemessen am echten Stylesheet mit der GRÖSSTEN Ansicht in Prod (45 Kacheln, „Training
             * 07.09.26" der DA) auf dem Samsung Tab S7 FE (1280 × 800):
             *
             * | | Seitenhöhe | zu scrollen | obere Zeile am Rasterende |
             * |---|---|---|---|
             * | mit Balken | 965 px | 165 px | **weg** (52 px über dem Rand) |
             * | ohne | **855 px** | 55 px | **noch da** (58 px im Bild) |
             *
             * Beim FB-Monatsevent (25 Kacheln) wird ohne ihn **gar nicht mehr gescrollt** (800 px
             * genau). Der Ausgang ist also weiterhin im Bild — nur oben statt unten.
             *
             * **Bekannte Grenze, bewusst in Kauf genommen:** Auf einem schmaleren Tablet
             * (1024 × 768) reichen 45 Kacheln auch ohne den Balken noch für 259 px Scrollen, und
             * dann ist die Zeile wieder weg. Die beiden Geräte des Vereins sind 1280 und 1472 px
             * breit; käme ein kleineres dazu, wäre der richtige Griff, die Bedienzeile anzuheften,
             * nicht den Balken zurückzuholen.
             */}
          </div>
        )}
      </div>
    </Shell>
  );
}
